/**
 * Timezone helpers. The backend stores everything in UTC ISO strings; this
 * module formats them in the user's preferred timezone.
 *
 * Priority: `USER_TIMEZONE` from Settings (persisted in Mongo) → browser
 * timezone (`Intl.DateTimeFormat().resolvedOptions().timeZone`) → UTC fallback.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

const BROWSER_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

let _cachedUserTz = null;
let _cachedAt = 0;

export async function getUserTimezone() {
  // Cache for 60s
  if (_cachedUserTz && Date.now() - _cachedAt < 60_000) return _cachedUserTz;
  try {
    const r = await api.get("/settings");
    const tzField = r.data?.USER_TIMEZONE;
    const configured = typeof tzField === "string" ? tzField : (tzField?.value || "");
    _cachedUserTz = configured?.trim() || BROWSER_TZ;
  } catch {
    _cachedUserTz = BROWSER_TZ;
  }
  _cachedAt = Date.now();
  return _cachedUserTz;
}

export function getBrowserTimezone() {
  return BROWSER_TZ;
}

export function formatInTz(isoOrDate, tz, opts = {}) {
  if (!isoOrDate) return "";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "";
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz || BROWSER_TZ,
    ...opts,
  };
  try {
    return new Intl.DateTimeFormat("en-GB", options).format(d);
  } catch {
    return d.toISOString();
  }
}

/** Short "in Xd Yh" or "3h 12m" countdown label. */
export function relativeUntil(iso) {
  if (!iso) return "";
  const now = Date.now();
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  let diff = Math.round((t - now) / 1000);
  const past = diff < 0;
  diff = Math.abs(diff);
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return past ? `${parts.join(" ")} ago` : `in ${parts.join(" ")}`;
}

/**
 * React hook: returns { userTz, browserTz, mismatch }.
 * mismatch=true when the browser TZ differs from the configured Settings TZ,
 * so the caller can render a "you're viewing from a different timezone" hint.
 */
export function useTimezone() {
  const [userTz, setUserTz] = useState(BROWSER_TZ);
  useEffect(() => {
    let cancelled = false;
    getUserTimezone().then((tz) => {
      if (!cancelled) setUserTz(tz);
    });
    return () => { cancelled = true; };
  }, []);
  return {
    userTz,
    browserTz: BROWSER_TZ,
    mismatch: userTz && BROWSER_TZ && userTz !== BROWSER_TZ,
  };
}
